<template>
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button @click="cancel">{{ readonly ? "Close" : "Cancel" }}</ion-button>
        </ion-buttons>
        <ion-title>{{ readonly ? "Schema" : schema ? "Edit Schema" : "New Schema" }}</ion-title>
        <ion-buttons slot="end">
          <ion-button v-if="!readonly" :disabled="saving" strong @click="save">
            {{ saving ? "Saving…" : "Save" }}
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding">
      <ion-list>
        <ion-item>
          <ion-label position="stacked">Name</ion-label>
          <ion-input v-model="name" placeholder="Schema name" :readonly="readonly" />
        </ion-item>
        <ion-item>
          <ion-label position="stacked">Description</ion-label>
          <ion-textarea v-model="description" placeholder="Optional description" :rows="2" :readonly="readonly" />
        </ion-item>
        <ion-item>
          <ion-label>Visibility</ion-label>
          <ion-select v-model="visibility" interface="popover" :disabled="readonly">
            <ion-select-option value="global">Global (all users)</ion-select-option>
            <ion-select-option value="private">Private (only me)</ion-select-option>
          </ion-select>
        </ion-item>
      </ion-list>

      <div class="sem-entries-section">
        <ion-list-header>
          <ion-label>Fields</ion-label>
          <ion-button v-if="!readonly && schema && localEntries.length > 1" fill="clear" size="small" @click="toggleReorderMode">
            <ion-icon slot="icon-only" :icon="reorderMode ? checkmarkOutline : swapVerticalOutline" />
          </ion-button>
          <ion-button v-if="!readonly" fill="clear" size="small" @click="openAddEntry">
            <ion-icon slot="icon-only" :icon="addOutline" />
          </ion-button>
        </ion-list-header>

        <!-- Reorder mode: saved entries only, drag to reorder -->
        <ion-reorder-group
          v-if="reorderMode"
          :disabled="false"
          @ion-item-reorder="onIonItemReorder($event)"
        >
          <ion-item v-for="entry in localEntries" :key="entry.id">
            <ion-icon :icon="entry.entry_type === 'schema_ref' ? libraryOutline : listOutline" slot="start" />
            <ion-label>{{ entry.entry_type === 'field' ? entry.name : (entry.sub_schema_name ?? `Schema #${entry.sub_schema_id}`) }}</ion-label>
            <ion-reorder slot="end" />
          </ion-item>
        </ion-reorder-group>

        <!-- Normal mode: saved entries (edit) or pending entries (create, not yet persisted) -->
        <template v-else>
          <ion-item v-for="entry in localEntries" :key="entry.id">
            <ion-icon :icon="entry.entry_type === 'schema_ref' ? libraryOutline : listOutline" slot="start" />
            <ion-label>
              <h3>{{ entry.entry_type === 'field' ? entry.name : (entry.sub_schema_name ?? `Schema #${entry.sub_schema_id}`) }}</h3>
              <p v-if="entry.entry_type === 'field'">
                {{ entry.field_type }}{{ entry.is_multi ? ' (multi)' : '' }}{{ entry.is_nullable === false ? ' · required' : '' }}
              </p>
              <p v-if="entry.entry_type === 'field' && entry.description" class="sem-entry-description">{{ entry.description }}</p>
              <p v-else-if="entry.entry_type !== 'field'">Sub-schema</p>
            </ion-label>
            <ion-button v-if="!readonly" fill="clear" slot="end" @click="openEditEntry(entry)">
              <ion-icon slot="icon-only" :icon="editIcon" title="Edit" alt="Edit" />
            </ion-button>
            <ion-button v-if="!readonly" fill="clear" color="danger" slot="end" @click="removeEntry(entry.id)">
              <ion-icon slot="icon-only" :icon="trashOutline" />
            </ion-button>
          </ion-item>

          <ion-item v-for="(entry, index) in pendingEntries" :key="`pending-${index}`">
            <ion-icon :icon="entry.entry_type === 'schema_ref' ? libraryOutline : listOutline" slot="start" />
            <ion-label>
              <h3>{{ entry.entry_type === 'field' ? entry.name : pendingSubSchemaName(entry.sub_schema_id) }}</h3>
              <p>
                <ion-text color="medium">Pending — added once the schema is saved</ion-text>
              </p>
            </ion-label>
            <ion-button fill="clear" color="danger" slot="end" @click="removePendingEntry(index)">
              <ion-icon slot="icon-only" :icon="trashOutline" />
            </ion-button>
          </ion-item>

          <ion-item v-if="localEntries.length === 0 && pendingEntries.length === 0">
            <ion-label style="color: var(--ion-color-medium)">No fields yet.</ion-label>
          </ion-item>

          <ion-button
            v-if="!readonly"
            expand="block"
            fill="outline"
            size="small"
            class="sem-add-field-repeat"
            @click="openAddEntry"
          >
            <ion-icon slot="start" :icon="addOutline" />
            Add field
          </ion-button>
        </template>
      </div>

      <ion-modal
        :is-open="entryModalOpen"
        :can-dismiss="canDismissExceptGesture"
        @did-dismiss="closeEntryModal"
      >
        <AddSchemaEntryModal
          :available-sub-schemas="availableSubSchemas"
          :entry="editingEntry"
          :schema-id="schema?.id ?? null"
          :initial-draft="entryDraft"
          @save="onEntryDraftSave"
          @saved="onEntrySaved"
          @close="closeEntryModal"
        />
      </ion-modal>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { canDismissExceptGesture } from "@/helper/modalDismiss";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
  IonButton, IonButtons, IonContent, IonHeader,
  IonIcon, IonInput, IonItem, IonLabel, IonList, IonListHeader, IonModal,
  IonPage, IonReorder, IonReorderGroup, IonSelect, IonSelectOption, IonText,
  IonTextarea, IonTitle, IonToolbar, alertController,
} from "@ionic/vue";
import { addOutline, checkmarkOutline, libraryOutline, listOutline, swapVerticalOutline, trashOutline } from "ionicons/icons";
import { editIcon } from "@/components/icons/renamed_ionicons";
import type { CustomSchemaOut, CustomSchemaEntryOut, SchemaFieldEntryCreate } from "@/models/custom-schemas";
import { useApiStore } from "@/stores/api";
import AddSchemaEntryModal from "@/components/modals/AddSchemaEntryModal.vue";
import { useDraft } from "@/composables/useDraft";
import { askToContinueDraft } from "@/helper/draftPrompt";
import type { Draft } from "@/helper/drafts";

const props = defineProps<{
  schema?: CustomSchemaOut | null;
  /** A saved draft of this dialog to continue, see `askToContinueDraft`. Read once, when the dialog opens. */
  initialDraft?: Draft | null;
}>();

const emit = defineEmits<{
  save: [schema: CustomSchemaOut];
  close: [];
}>();

const api = useApiStore();

/** Built-in schemas describe our own tables: shown for reference only, nothing in here can change them. */
const readonly = computed(() => props.schema?.is_builtin === true);

const name = ref(props.schema?.name ?? "");
const description = ref(props.schema?.description ?? "");
const visibility = ref<"global" | "private">(props.schema?.visibility ?? "global");
const saving = ref(false);
const localEntries = ref<CustomSchemaEntryOut[]>([...(props.schema?.entries ?? [])]);
const pendingEntries = ref<SchemaFieldEntryCreate[]>([]);
const reorderMode = ref(false);
const addEntryModalOpen = ref(false);
const editingEntry = ref<SchemaEntryOut | null>(null);
const entryModalOpen = computed(() => addEntryModalOpen.value || editingEntry.value !== null);

const entryDraft = ref<Draft | null>(null);

async function openAddEntry() {
  const { choice, draft } = await askToContinueDraft("schema-entry", `${props.schema?.id ?? "new"}:new`);
  if (choice === "abort") return;
  entryDraft.value = draft;
  addEntryModalOpen.value = true;
}

async function openEditEntry(entry: SchemaEntryOut) {
  const { choice, draft } = await askToContinueDraft("schema-entry", `${props.schema?.id ?? "new"}:${entry.id}`);
  if (choice === "abort") return;
  entryDraft.value = draft;
  editingEntry.value = entry;
}

// Only what is not saved yet: the edited settings and the pending entries. The saved entries are server state.
const draftState = useDraft({
  kind: props.schema ? "schema-edit" : "schema-create",
  scope: () => String(props.schema?.id ?? "new"),
  label: () => name.value.trim() || (props.schema ? `Schema #${props.schema.id}` : "New schema"),
  snapshot: () => ({
    name: name.value,
    description: description.value,
    visibility: visibility.value,
    pendingEntries: pendingEntries.value,
  }),
});
let finished = false;

function applyInitialDraft() {
  const saved = props.initialDraft?.data;
  if (!saved) return;
  name.value = (saved.name as string) ?? name.value;
  description.value = (saved.description as string) ?? description.value;
  visibility.value = (saved.visibility as "global" | "private") ?? visibility.value;
  pendingEntries.value = (saved.pendingEntries as SchemaFieldEntryCreate[]) ?? [];
}
applyInitialDraft();

function cancel() {
  finished = true;
  draftState.cancel();
  emit("close");
}
// closed by the backdrop or a swipe instead of the Cancel button
onBeforeUnmount(() => {
  if (!finished) draftState.cancel();
});

function closeEntryModal() {
  addEntryModalOpen.value = false;
  editingEntry.value = null;
}

const availableSubSchemas = computed(() =>
  api.schemas.filter((s) => s.id !== props.schema?.id)
);

function pendingSubSchemaName(subSchemaId?: number | null): string {
  const s = availableSubSchemas.value.find((s) => s.id === subSchemaId);
  return s?.name ?? `Schema #${subSchemaId}`;
}

async function showSaveError(e: any) {
  const detail = e?.response?.data?.detail;
  const message = (typeof detail === "object" ? detail?.message : detail) ?? e?.message ?? "Unknown error.";
  const alert = await alertController.create({
    header: "Failed to save",
    message,
    buttons: ["OK"],
  });
  await alert.present();
}

watch(() => props.schema, (s) => {
  if (s) {
    name.value = s.name;
    description.value = s.description ?? "";
    visibility.value = s.visibility;
    localEntries.value = [...s.entries];
  }
});

onMounted(() => {
  api.fetchSchemas();
});

// Set once a brand-new schema has been created, so a retry after a partial failure
// (e.g. one of several pendingEntries fails to add) updates that schema instead of
// calling createSchema() again and leaving a duplicate behind.
const createdSchema = ref<FieldSchemaOut | null>(null);

async function save() {
  if (!name.value.trim()) return;
  saving.value = true;
  try {
    const targetSchema = props.schema ?? createdSchema.value;
    let result: CustomSchemaOut;
    if (targetSchema) {
      result = await api.updateSchema({
        schemaId: targetSchema.id,
        patch: { name: name.value.trim(), description: description.value || null, visibility: visibility.value },
      });
    } else {
      result = await api.createSchema({ name: name.value.trim(), description: description.value || null, visibility: visibility.value });
      createdSchema.value = result;
    }
    if (pendingEntries.value.length) {
      for (const entry of [...pendingEntries.value]) {
        await api.addSchemaEntry({ schemaId: result.id, entry });
        pendingEntries.value = pendingEntries.value.filter((e) => e !== entry);
      }
      const updated = api.schemas.find((s) => s.id === result.id);
      if (updated) result = updated;
    }
    finished = true;
    draftState.clear();
    emit("save", result);
  } catch (e: any) {
    await showSaveError(e);
  } finally {
    saving.value = false;
  }
}

async function onEntryDraftSave(entry: SchemaFieldEntryCreate) {
  if (props.schema) {
    try {
      await api.addSchemaEntry({ schemaId: props.schema.id, entry });
      const updated = api.schemas.find((s) => s.id === props.schema!.id);
      if (updated) localEntries.value = [...updated.entries];
      addEntryModalOpen.value = false;
    } catch (e: any) {
      // Keep the modal open on failure so the user doesn't lose the draft and can retry.
      await showSaveError(e);
    }
  } else {
    pendingEntries.value = [...pendingEntries.value, entry];
    addEntryModalOpen.value = false;
  }
}

function onEntrySaved() {
  closeEntryModal();
  if (!props.schema) return;
  const updated = api.schemas.find((s) => s.id === props.schema!.id);
  if (updated) localEntries.value = [...updated.entries];
}

function removePendingEntry(index: number) {
  pendingEntries.value = pendingEntries.value.filter((_, i) => i !== index);
}

async function removeEntry(entryId: number) {
  if (!props.schema) return;
  await api.deleteSchemaEntry({ schemaId: props.schema.id, entryId });
  localEntries.value = localEntries.value.filter((e) => e.id !== entryId);
}

function onIonItemReorder(event: CustomEvent) {
  const { from, to } = event.detail;
  const newList = [...localEntries.value];
  const [moved] = newList.splice(from, 1);
  newList.splice(to, 0, moved);
  localEntries.value = newList;
  event.detail.complete(false);
}

async function toggleReorderMode() {
  if (!reorderMode.value) {
    reorderMode.value = true;
    return;
  }
  if (props.schema) {
    await api.reorderSchemaEntries({ schemaId: props.schema.id, entryIds: localEntries.value.map((e) => e.id) });
  }
  reorderMode.value = false;
}
</script>

<style scoped>
.sem-entries-section {
  margin-top: 16px;
}

.sem-add-field-repeat {
  margin-top: 8px;
}

.sem-entry-description {
  color: var(--ion-color-medium);
  font-style: italic;
}
</style>
